import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { styled } from '@mui/material/styles';
import { palette } from 'theme/palette';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { useTariffSummaryAlertConfiguration } from '../hooks/useTariffSummaryAlertConfiguration';
import { AutoHideSnackbar } from 'src/common/Snackbar/AutoHideSnackbar';
import { AlertTitle } from 'src/common/Snackbar/AlertTitle';
import { createPortal } from 'react-dom';
import { isNil } from 'lodash';
import { useTariffAccountRef } from '../../hooks/useTariffAccountRef';
import { useExternalRowSelectionApi } from 'src/common/components/ConfigurableTable/Components/Rows/ExternalRowSelection/hooks/useExternalRowSelectionApi';
import { pxToRem } from 'src/common/utils/fonts';
import { DEFAULT_BORDER_RADIUS } from 'src/theme/constants';
import { Dialog } from 'src/common/components/Dialog/Dialog';
import Alert from '@mui/material/Alert';

const ALERT_PERCENTAGE_OPTIONS = [80, 100];

const RowContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(16),
}));

const ValuesRadioGroup = styled(RadioGroup)<{ enabled?: boolean }>(
  ({ theme, enabled = false }) => ({
    padding: theme.spacing(5, 10),
    marginLeft: 'auto',
    borderRadius: DEFAULT_BORDER_RADIUS,
    background: '#ececf0',
    opacity: enabled ? 1 : 0.5,
    justifyContent: 'center',
    gap: theme.spacing(10),

    '& label': {
      margin: 0,
    },
  }),
);

const StyledBoxRadio = styled(Box)<{ selected?: boolean; enabled?: boolean }>(
  ({ theme, selected = false, enabled = true }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6),
    borderRadius: DEFAULT_BORDER_RADIUS,
    background: selected ? theme.palette.white : '#ececf0',
    boxShadow: selected
      ? `${theme.spacing(2)} ${theme.spacing(2)} ${theme.spacing(5)} rgba(0,0,0,0.1)`
      : 'none',
    width: theme.spacing(60),
    cursor: enabled ? 'pointer' : 'default',
    fontSize: pxToRem(14),
    opacity: enabled ? 1 : 0.5,
    transition: 'all 0.2s ease-in-out',
  }),
);

const StyledRadio = styled(Radio)(() => ({
  width: 0,
  height: 0,
  opacity: 0,
  padding: 0,
}));

const AlertInfoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(5),
  alignItems: 'center',
  marginTop: theme.spacing(10),
}));

type ConfigurationItemProps = {
  name: AlertType;
  configuration: number | null;
  onChange: any;
  disabled?: boolean;
  label?: string;
};

const ConfigurationItem = ({
  name,
  configuration,
  onChange,
  disabled,
  label,
}: ConfigurationItemProps) => {
  const { t } = useTranslation();

  const enabled = !isNil(configuration);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number.parseInt(event.target.value, 10);
    if (!enabled || disabled) return;
    onChange(name, value);
  };

  const prevNotNullValue = useRef(80);

  if (!isNil(configuration)) {
    prevNotNullValue.current = configuration;
  }

  return (
    <Box sx={{ paddingTop: 24, position: 'relative' }} data-testid={`${name}-section`}>
      <RowContainer>
        <FormControlLabel
          data-testid={`${name}-toggle-button`}
          control={
            <Switch
              checked={enabled}
              disabled={disabled}
              onChange={() => onChange(name, configuration ? null : prevNotNullValue.current)}
            />
          }
          label={
            <Typography variant="h3">{t(`tariffSummary.table.usageAlert.${name}`)}</Typography>
          }
        />

        <FormControl disabled={!enabled || disabled} sx={{ marginLeft: 'auto' }}>
          <ValuesRadioGroup
            row
            onChange={handleRadioChange}
            name={`${name}-radio-group`}
            data-testid={`${name}-radio-group`}
            enabled={enabled}
            aria-label={label}
          >
            {ALERT_PERCENTAGE_OPTIONS.map((val) => (
              <FormControlLabel
                key={val}
                value={val}
                control={
                  <StyledRadio checked={(configuration ?? prevNotNullValue.current) === val} />
                }
                label={
                  <StyledBoxRadio
                    selected={(configuration ?? prevNotNullValue.current) === val}
                    enabled={enabled}
                    data-testid={`${name}-radio-group-${val}-option`}
                  >
                    {`${val}%`}
                  </StyledBoxRadio>
                }
              />
            ))}
          </ValuesRadioGroup>
        </FormControl>
      </RowContainer>
      {enabled && (
        <Typography variant="body2">
          {t(`tariffSummary.table.usageAlert.${name}Info`, { percentage: configuration })}
        </Typography>
      )}
      <Divider sx={{ marginTop: 24 }} />
    </Box>
  );
};

type AlertConfiguration = {
  dataThreshold: number | null;
  voiceThreshold: number | null;
  smsThreshold: number | null;
};

const DEFAULT_ALERT_CONFIGURATION: AlertConfiguration = {
  dataThreshold: null,
  voiceThreshold: null,
  smsThreshold: null,
};

type AlertType = keyof typeof DEFAULT_ALERT_CONFIGURATION;

type TariffSummaryAlertConfigurationDialogContentProps =
  TariffSummaryAlertConfigurationDialogProps & {
    data: SelectedTariffData[];
    onSuccess: () => void;
    onError: () => void;
  };

const TariffSummaryAlertConfigurationDialogContent = ({
  data,
  open,
  onSuccess,
  onError,
  onClose,
}: TariffSummaryAlertConfigurationDialogContentProps) => {
  const { t } = useTranslation();
  const accountRef = useTariffAccountRef();

  const [alertConfiguration, setAlertConfiguration] = useState<AlertConfiguration>(
    data[0].alertConfiguration ?? DEFAULT_ALERT_CONFIGURATION,
  );

  const numberOfAffectedTariffs = data.length;

  const handleClose = () => {
    setAlertConfiguration(DEFAULT_ALERT_CONFIGURATION);
    onClose();
  };

  const handleConfigurationSuccess = () => {
    onSuccess();
    handleClose();
  };

  const handleConfigurationError = () => {
    onError();
    handleClose();
  };

  const { configureAlert, isPending } = useTariffSummaryAlertConfiguration({
    onSuccess: handleConfigurationSuccess,
    onError: handleConfigurationError,
    accountRef,
  });

  const handleChange = (name: string, configuration: { enabled: boolean; value: number }) => {
    setAlertConfiguration((prev) => ({
      ...prev,
      [name]: configuration,
    }));
  };

  const handleConfirm = () => {
    const tariffCodes = data.map(
      ({ bundleProductCode }: { bundleProductCode: string }) => bundleProductCode,
    );

    configureAlert({ tariffCodes, alertConfiguration });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      size="small"
      title={t('tariffSummary.table.usageAlert.configureAlert')}
      actions={
        <>
          <Button
            color="secondary"
            onClick={handleClose}
            data-testid="Cancel configuration button"
            disabled={isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            data-testid="Apply configuration button"
            disabled={isPending}
          >
            {t('tariffSummary.table.usageAlert.applyConfiguration', {
              count: numberOfAffectedTariffs,
            })}
          </Button>
        </>
      }
    >
      <Typography variant="body2">
        {t('tariffSummary.table.usageAlert.configureCountInfo', {
          count: numberOfAffectedTariffs,
        })}
      </Typography>

      <Typography variant="body2">
        {t('tariffSummary.table.usageAlert.configureTypeInfo')}
      </Typography>
      {numberOfAffectedTariffs > 1 && (
        <Alert
          severity="warning"
          icon={<InfoOutlineIcon fontSize={'inherit'} />}
          sx={{ marginTop: 8 }}
        >
          {t('tariffSummary.table.usageAlert.savingWarning')}
        </Alert>
      )}

      <ConfigurationItem
        name="dataThreshold"
        configuration={alertConfiguration.dataThreshold}
        onChange={handleChange}
        disabled={isPending}
        label="Data threshold"
      />
      <ConfigurationItem
        name="voiceThreshold"
        configuration={alertConfiguration.voiceThreshold}
        onChange={handleChange}
        disabled={isPending}
        label="Voice threshold"
      />
      <ConfigurationItem
        name="smsThreshold"
        configuration={alertConfiguration.smsThreshold}
        onChange={handleChange}
        disabled={isPending}
        label="SMS threshold"
      />

      <AlertInfoContainer>
        <InfoOutlineIcon sx={{ fill: palette.blue['600'] }} />
        <Typography variant="body2">
          {t('tariffSummary.table.usageAlert.configureAlertInfo')}
        </Typography>
      </AlertInfoContainer>
    </Dialog>
  );
};

type SelectedTariffData = {
  bundleProductCode: string;
  alertConfiguration: {
    dataThreshold: number | null;
    smsThreshold: number | null;
    voiceThreshold: number | null;
  };
};

type TariffSummaryAlertConfigurationDialogProps = {
  data?: SelectedTariffData[];
  open: boolean;
  onClose: () => void;
};

export const TariffSummaryAlertConfigurationDialog = (
  props: TariffSummaryAlertConfigurationDialogProps,
) => {
  const { t } = useTranslation();
  const selectedCountRef = useRef(props.data?.length);
  const { unSelectAll } = useExternalRowSelectionApi();

  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  const shouldShowModal = props.open && props.data;

  return (
    <>
      {shouldShowModal && (
        <TariffSummaryAlertConfigurationDialogContent
          {...props}
          data={props.data!}
          onSuccess={() => {
            selectedCountRef.current = props.data!.length;
            setIsSuccess(true);
            unSelectAll();
          }}
          onError={() => setIsError(true)}
        />
      )}
      {(isSuccess || isError) &&
        createPortal(
          <Stack sx={{ position: 'absolute', top: 0, left: 0 }}>
            <AutoHideSnackbar
              severity={isSuccess ? 'success' : 'error'}
              open={isSuccess || isError}
              onClose={() => {
                setIsSuccess(false);
                setIsError(false);
              }}
            >
              <AlertTitle>
                {isSuccess
                  ? t('tariffSummary.table.usageAlert.alertConfigurationSuccess', {
                      count: selectedCountRef.current || 1,
                    })
                  : t('tariffSummary.table.usageAlert.alertConfigurationError')}
              </AlertTitle>
            </AutoHideSnackbar>
          </Stack>,
          document.body,
        )}
    </>
  );
};
